import { useState, useMemo, useEffect } from 'react'
import useSWR from 'swr'
import {
  IndexColumn,
  DateColumn,
  CampaignNameColumn,
  MediaSourceColumn,
  CountryColumn,
  ReasonColumn,
  CreatedAtColumn,
  OptColumn,
  CampaignStateColumn,
} from '@/components/roas/notes/columns'
import Loader from '@/components/zthd/Loader'
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import DataTableWithBorder from '@/components/zthd/data-table/data-table-with-border'
import AppSelect from '@/components/zthd/app-select'
import { useUrlParamsStore } from '@/stores/url-params-store'
import { DropdownCountry1 } from '@/components/DropdownCountry'
import { Button } from '@/components/ui/button'
import { Download } from 'lucide-react'
import useSWRMutation from 'swr/mutation'
import { blobFetcher, postFetcher } from '@/hooks'
import { useToast } from '@/hooks/use-toast'
import ImportNoteDialog from '@/components/roas/notes/import-note-dialog'
import PlatformChangesDialog from './platform-changes-dialog'
import {
  getCoverageState,
  getPlatformChangePlatform,
  isPlatformChangeSupported,
} from './platform-changes'

const NotesPage = () => {
  const [page, setPage] = useState(1)
  const pageSize = 20
  const params = useUrlParamsStore((state) => state.params)
  const setParam = useUrlParamsStore((state) => state.setParam)
  const resetParams = useUrlParamsStore((state) => state.resetParams)
  const removeParam = useUrlParamsStore((state) => state.removeParam)
  const { toast } = useToast()
  const [selectedNote, setSelectedNote] = useState(null)

  const { data, isLoading, mutate } = useSWR([`/troas/notes`, params])
  const notes = useMemo(() => data?.data?.notes || [], [data])
  const [sorting, setSorting] = useState([{ id: 'createdAt', desc: true }])

  const columns = useMemo(() => [
    IndexColumn,
    DateColumn,
    CampaignNameColumn,
    CampaignStateColumn,
    MediaSourceColumn,
    CountryColumn,
    ReasonColumn,
    {
      id: 'platformChanges',
      header: 'Platform Changes',
      cell: ({ row }) => {
        const note = row.original
        const platform = getPlatformChangePlatform(note.mediaSource)
        const count = note.platformChanges?.count
        const coverageStatus = note.platformChanges?.coverageStatus
        const coverage = getCoverageState(coverageStatus)
        const hasNonCompleteCoverage = coverageStatus && coverageStatus !== 'available'

        if (!isPlatformChangeSupported(note.mediaSource)) {
          return <span className="text-muted-foreground">N/A</span>
        }

        return (
          <Button
            variant="link"
            className="h-auto p-0"
            data-clickable-cell="true"
            onClick={() => setSelectedNote(note)}
          >
            {platform}{typeof count === 'number' ? ` (${count})` : ''}
            {hasNonCompleteCoverage ? ` · ${coverage.label}` : ''}
          </Button>
        )
      },
    },
    CreatedAtColumn,
    OptColumn,
  ], [])

  // 更新 URL 参数的函数
  const updateSearchParams = (key, value) => {
    if (value === 'total' || value === 'all') {
      removeParam(key)
    } else {
      setParam(key, value)
    }
  }

  useEffect(() => {
    resetParams()
  }, [resetParams])

  const table = useReactTable({
    data: notes,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  })

  const [result, setResult] = useState({
    successNotes: [],
    failedNotes: [],
  })

  const { trigger: exportTrigger, isMutating: isExporting } = useSWRMutation(
    ['/troas/notes/export', { pid: params.pid, country: params.country }],
    blobFetcher,
  )

  const { trigger: importTrigger, isMutating: isImporting } = useSWRMutation(
    '/troas/notes/import',
    postFetcher,
  )

  const handleExport = async () => {
    if (!params.pid || !params.country) return

    const { blob, fileName } = await exportTrigger()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', fileName || `notes-${params.pid}-${params.country}-${new Date().toISOString().split('T')[0]}.xlsx`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleImport = async (noteInput) => {
    try {
      const importResult = await importTrigger({
        date: noteInput.date,
        reason: noteInput.reason,
      })
      setResult({
        successNotes: importResult.successNotes,
        failedNotes: importResult.failedNotes,
      })
      await mutate()
    } catch (error) {
      setResult({
        successNotes: [],
        failedNotes: [],
      })
    }
  }

  const handleClose = () => {
    setResult({
      successNotes: [],
      failedNotes: [],
    })
  }

  if (isLoading) return <Loader />

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <AppSelect
            value={params.pid || ''}
            onValueChange={(value) => updateSearchParams('pid', value)}
          />
          <DropdownCountry1
            value={params.country || ''}
            update={(value) => updateSearchParams('country', value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <ImportNoteDialog onSubmit={handleImport} isImporting={isImporting} result={result} onClose={handleClose} />
          {params.pid && params.country && (
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={isExporting}
            >
              <Download className="mr-2 h-4 w-4" />
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
          )}
        </div>
      </div>
      <div className="container mx-auto">
        <DataTableWithBorder table={table} />
      </div>
      <PlatformChangesDialog
        note={selectedNote}
        open={Boolean(selectedNote)}
        onOpenChange={(open) => {
          if (!open) setSelectedNote(null)
        }}
      />
    </div>
  )
}

export default NotesPage
